import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination from "@/components/ventrix/Pagination";
import { formatDate, formatDateShort, EmptyState } from "@/components/ventrix/atoms";
import { Mail, Send, Inbox } from "lucide-react";

const LIMIT = 50;
const TABS = [
  { v: "pending", label: "Pending" },
  { v: "sent", label: "Sent" },
  { v: "dismissed", label: "Dismissed" },
  { v: "all", label: "All" },
];

export default function ShipmentEmails({ section = "admin" }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState("");
  const [reviewing, setReviewing] = useState(null);

  const load = () => {
    setItems(null);
    api.get(`/v1/shipment-emails?status=${status}&limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status, offset]); // eslint-disable-line

  const pick = (v) => { setStatus(v); setOffset(0); };

  const dismiss = async (id) => {
    setBusy(id);
    try {
      await api.post(`/v1/shipment-emails/${id}/dismiss`);
      toast.success("Email dismissed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <PortalLayout section={section}>
      <div className="vtx-eyebrow">/ {section === "ops" ? "Ops" : "Admin"} · Customer emails</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><Mail size={24} /> Email review</h1>
      <p className="text-sm text-[#475569] mt-1">Shipment updates don't email customers automatically. Review — and edit — each message here, then send it.</p>

      <div className="mt-6 flex flex-wrap gap-1.5" data-testid="email-tabs">
        {TABS.map(t => (
          <button key={t.v} onClick={() => pick(t.v)} data-testid={`tab-${t.v}`}
            className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${status === t.v ? "bg-[#0E2F76] text-white" : "bg-white text-[#0E2F76] border border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`}
            style={{ borderRadius: 2 }}>{t.label}</button>
        ))}
      </div>

      <div className="vtx-card mt-4 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map(i => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && items.length === 0 && (
          <EmptyState icon={Inbox} title="Nothing to review" hint="Customer emails for shipment updates appear here for your team to confirm before sending." />
        )}
        {items && items.length > 0 && (
          <table className="vtx-table w-full">
            <thead><tr><th>Shipment</th><th>Update</th><th>Recipient</th><th>ETA</th><th>Queued</th><th></th></tr></thead>
            <tbody>
              {items.map(e => (
                <tr key={e.id} data-testid={`email-row-${e.id}`}>
                  <td className="font-mono font-semibold text-[#0E2F76]">{e.tracking_number}</td>
                  <td className="text-sm">{e.status_label || e.status_code}</td>
                  <td className="font-mono text-xs">{e.to || "—"}</td>
                  <td className="text-xs text-[#475569] whitespace-nowrap">{e.shipment?.eta ? formatDateShort(e.shipment.eta) : "—"}</td>
                  <td className="text-xs text-[#475569] whitespace-nowrap">{formatDate(e.created_at)}</td>
                  <td>
                    {e.status === "sent" ? (
                      <span className="text-xs text-[#166534] font-bold">Sent</span>
                    ) : e.status === "dismissed" ? (
                      <span className="text-xs text-[#475569]">Dismissed</span>
                    ) : (
                      <div className="flex gap-3">
                        <button className="text-xs text-[#0E2F76] font-bold hover:underline disabled:opacity-40" disabled={busy === e.id}
                          onClick={() => setReviewing(e)} data-testid={`email-review-${e.id}`}>Review &amp; send</button>
                        <button className="text-xs text-[#d41f26] font-bold hover:underline disabled:opacity-40" disabled={busy === e.id}
                          onClick={() => dismiss(e.id)} data-testid={`email-dismiss-${e.id}`}>Dismiss</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>

      <ReviewDialog email={reviewing} onClose={() => setReviewing(null)} onSent={() => { setReviewing(null); load(); }} />
    </PortalLayout>
  );
}

function ReviewDialog({ email, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (email) { setSubject(email.subject || ""); setMessage(email.message || ""); }
  }, [email]);

  if (!email) return null;
  const s = email.shipment || {};

  const send = async () => {
    if (!email.to) { toast.error("This shipment has no customer email on file."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/v1/shipment-emails/${email.id}/send`, {
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      if (data.sent) { toast.success(`Email sent to ${email.to}`); onSent(); }
      else { toast.error(data.message || "The email couldn't be sent."); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!email} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="email-review-dialog">
        <DialogHeader><DialogTitle>Review customer email</DialogTitle></DialogHeader>
        <div className="vtx-card p-4 bg-[#F4FEFF] text-sm text-[#334155]">
          <div className="font-semibold text-[#0E2F76]">{s.tracking_number} · {email.status_label || email.status_code}</div>
          <div className="mt-1 text-xs text-[#475569]">
            {s.origin} → {s.destination}{s.eta ? <> · ETA {formatDateShort(s.eta)}</> : null}
            {email.event?.location ? <> · {email.event.location}</> : null}
          </div>
          <div className="mt-1 text-xs text-[#475569]">To: <span className="font-mono text-[#0E2F76]">{email.to || "— no email on file —"}</span></div>
        </div>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Subject</span>
          <input className="vtx-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" maxLength={300} />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Message</span>
          <textarea className="vtx-input" rows={5} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="email-message" />
        </label>
        <p className="text-[11px] text-[#475569] -mt-1">This text becomes the body of a branded Ventrix email with the shipment details and a tracking button added automatically.</p>
        <DialogFooter>
          <button className="vtx-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={send} disabled={sending || !email.to} data-testid="email-send-confirm">
            <Send size={14} /> {sending ? "Sending…" : "Send email"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
